import { MATERIALS, type MaterialId, type MaterialInventory } from './materials';

/** One shared chemical scale preserves physical mass when inventories migrate. */
export const MOLES_PER_INVENTORY_UNIT = 1.8e20;
export const PASCALS_PER_BAR = 100_000;
/** Artistic mass-extinction scale; cloudOpacity remains a relative material coefficient. */
export const CLOUD_OPACITY_UNIT_M2_PER_KG = 0.04;

export interface ClimateGeometry {
  readonly radiusMeters: number;
  readonly surfaceGravity: number;
  /** Elevation range of the exchangeable surface reservoir, not core depth. */
  readonly reliefMeters: number;
  readonly terrainSeed: number;
}

export function climateGeometry(radiusMeters: number, surfaceGravity: number, reliefMeters: number, terrainSeed = 0.47): ClimateGeometry {
  if (![radiusMeters, surfaceGravity, reliefMeters].every(value => Number.isFinite(value) && value > 0)) {
    throw new RangeError('Climate radius, gravity, and relief must be finite and positive.');
  }
  if (!Number.isFinite(terrainSeed)) throw new RangeError('Terrain seed must be finite.');
  return { radiusMeters, surfaceGravity, reliefMeters, terrainSeed };
}

export function surfaceArea(geometry: ClimateGeometry): number { return 4 * Math.PI * geometry.radiusMeters ** 2; }
export function materialMassKg(material: MaterialId, amount: number): number {
  return amount * MOLES_PER_INVENTORY_UNIT * MATERIALS[material].molarMassKg;
}
export function pressurePerUnit(material: MaterialId, geometry: ClimateGeometry): number {
  return materialMassKg(material, 1) * geometry.surfaceGravity / surfaceArea(geometry) / PASCALS_PER_BAR;
}
export function atmosphericColumn(stocks: readonly MaterialInventory[], geometry: ClimateGeometry): {
  massKg: number; pressureBar: number; gasUnits: number;
} {
  let massKg = 0, gasUnits = 0;
  for (const stock of stocks) {
    gasUnits += stock.phases.gas + stock.phases.plasma;
    massKg += materialMassKg(stock.material, stock.phases.gas + stock.phases.plasma + stock.suspended.solid + stock.suspended.liquid);
  }
  return { massKg, gasUnits, pressureBar: massKg * geometry.surfaceGravity / surfaceArea(geometry) / PASCALS_PER_BAR };
}

/** Finite equal-area terrain samples with exact piecewise-linear volume fill.
 * Heights and prefix sums are immutable and shared across climate instances. */
export class BasinModel {
  private readonly heights: number[];
  private readonly prefix: number[];
  public constructor(heights: readonly number[]) {
    if (heights.length === 0 || heights.some(height => !Number.isFinite(height) || height < 0 || height > 1)) {
      throw new RangeError('Basin heights must be normalized finite terrain samples.');
    }
    this.heights = [...heights].sort((a, b) => a - b);
    this.prefix = [0];
    for (const height of this.heights) this.prefix.push(this.prefix.at(-1)! + height);
  }
  public fill(volumeM3: number, geometry: ClimateGeometry): { levelMeters: number; normalizedLevel: number; coverage: number } {
    if (!Number.isFinite(volumeM3) || volumeM3 < 0) throw new RangeError('Liquid volume must be finite and non-negative.');
    if (volumeM3 === 0) return { levelMeters: 0, normalizedLevel: 0, coverage: 0 };
    const count = this.heights.length;
    const depth = volumeM3 / surfaceArea(geometry) / geometry.reliefMeters;
    let low = 1, high = count;
    while (low < high) {
      const middle = (low + high) >>> 1;
      const toNextHeight = (this.heights[middle]! * middle - this.prefix[middle]!) / count;
      if (toNextHeight <= depth) low = middle + 1;
      else high = middle;
    }
    const level = (depth * count + this.prefix[low]!) / low;
    return { levelMeters: level * geometry.reliefMeters, normalizedLevel: level, coverage: low / count };
  }
}
